import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ProblemLines = Record<number, number>;

interface PracticeState {
    isPracticing: boolean;
    isPaused: boolean;
    currentLineIndex: number;
    currentActualIndex: number;
    isDrillingProblems: boolean;
    problemLines: ProblemLines;
    problemLineIndices: number[];
    isMyTurn: boolean;
    revealedCueLineIndices: number[];
    resumeLineIndex: number | null;
    lineProgress: {
        chunks: string[];
        covered: number[];
        percentage: number;
    };
}

const initialState: PracticeState = {
    isPracticing: false,
    isPaused: false,
    currentLineIndex: 0,
    currentActualIndex: 0,
    isDrillingProblems: false,
    problemLines: {},
    problemLineIndices: [],
    isMyTurn: false,
    revealedCueLineIndices: [],
    resumeLineIndex: null,
    lineProgress: {
        chunks: [],
        covered: [],
        percentage: 0,
    },
};

const practiceSlice = createSlice({
    name: 'practice',
    initialState,
    reducers: {
        startPractice(state) {
            state.isPracticing = true;
            state.isPaused = false;
        },
        stopPractice(state) {
            state.isPracticing = false;
            state.isMyTurn = false;
        },
        pausePractice(state) {
            state.isPaused = true;
        },
        resumePractice(state) {
            state.isPaused = false;
        },
        setLineIndex(state, action: PayloadAction<number>) {
            state.currentLineIndex = action.payload;
        },
        setActualIndex(state, action: PayloadAction<number>) {
            state.currentActualIndex = action.payload;
        },
        advanceLine(state) {
            state.currentLineIndex += 1;
        },
        setIsMyTurn(state, action: PayloadAction<boolean>) {
            state.isMyTurn = action.payload;
        },
        addProblemLine(state, action: PayloadAction<number>) {
            const idx = action.payload;
            state.problemLines[idx] = (state.problemLines[idx] || 0) + 1;
        },
        resetProblemLines(state) {
            state.problemLines = {};
        },
        setDrillMode(state, action: PayloadAction<{ enabled: boolean; indices?: number[] }>) {
            state.isDrillingProblems = action.payload.enabled;
            if (action.payload.indices) {
                state.problemLineIndices = action.payload.indices;
            }
        },
        revealCueLine(state, action: PayloadAction<number>) {
            if (!state.revealedCueLineIndices.includes(action.payload)) {
                state.revealedCueLineIndices.push(action.payload);
            }
        },
        clearRevealedCues(state) {
            state.revealedCueLineIndices = [];
        },
        setResumeIndex(state, action: PayloadAction<number | null>) {
            state.resumeLineIndex = action.payload;
        },
        setLineProgress(state, action: PayloadAction<{ chunks: string[]; covered: number[]; percentage: number }>) {
            state.lineProgress = action.payload;
        },
        resetLineProgress(state) {
            state.lineProgress = {
                chunks: [],
                covered: [],
                percentage: 0,
            };
        },
    },
});

export const {
    startPractice, stopPractice, pausePractice, resumePractice,
    setLineIndex, setActualIndex, advanceLine,
    setIsMyTurn, addProblemLine, resetProblemLines,
    setDrillMode, revealCueLine, clearRevealedCues, setResumeIndex,
    setLineProgress, resetLineProgress,
} = practiceSlice.actions;

export default practiceSlice.reducer;
